import cx from 'classnames'
import React, { createRef, useState } from 'react'

import { useAppSelector } from '../../../hooks'
import { selectCurrentPlaylistSlug } from '../../../selectors/current-play-selectors'
import { LeaderboardCell, selectMatchStatsByTrack } from '../../../selectors/leaderboard-selectors'
import { selectScoreVersion } from '../../../selectors/session-selectors'
import { TrackPath } from '../../../types'
import TrackBadge from '../../widgets/TrackBadge'
import GuestPlayerSetup from '../match-info/GuestPlayerSetup'
import { selectCurrentUsername } from '../../../selectors/session-selectors'
import PlayStatusDetails from './PlayStatusDetails'

type Props = {
  matchSlug: string
  isPrintReady?: boolean
}
type SelectedCellMap = {
  [key: string]: boolean
}
const StatsByTrackTable = ({ matchSlug: compoundMatchSlug, isPrintReady = false }: Props) => {
  const [matchOwner, matchSlug] = compoundMatchSlug.split('/')
  const currUsername = useAppSelector(selectCurrentUsername)
  const isOwnedMatch = matchOwner === currUsername
  const scoreVersion = useAppSelector(selectScoreVersion)
  const leaderboardData = useAppSelector(selectMatchStatsByTrack({ matchOwner, matchSlug }))
  const {
    colLabels,
    rowLabels,
    dataRows,
    playerCounts: { guestCount },
  } = leaderboardData
  const currentPlaylistSlug = useAppSelector(selectCurrentPlaylistSlug)
  const [selectedCells, setSelectedCells] = useState<SelectedCellMap>({})
  const scoreEditRef = createRef<HTMLInputElement>()
  React.useEffect(() => {
    const elem = scoreEditRef.current
    if (elem) {
      elem.select()
    }
  })

  let inDefaultOpen = false
  const initialRowState = rowLabels.map(({ slug, trackInfo }, index) => {
    if (!trackInfo) {
      inDefaultOpen = slug === currentPlaylistSlug
    }
    return inDefaultOpen
  })
  const [playlistRowState, setPlaylistRowState] = useState<boolean[]>(initialRowState)

  const togglePlaylist = (playlistRow: number) => {
    const newState = !playlistRowState[playlistRow]
    const newRowState = [...playlistRowState]
    newRowState[playlistRow] = newState
    for (let row = playlistRow + 1; row < rowLabels.length; row++) {
      const { trackInfo } = rowLabels[row]
      if (!trackInfo) {
        break
      }
      newRowState[row] = newState
    }
    setPlaylistRowState(newRowState)
  }
  const getRowLabel = (rowIndex: number) => {
    const { title, trackInfo } = rowLabels[rowIndex]
    if (!!trackInfo && !playlistRowState[rowIndex]) {
      return null
    }
    return (
      <td className="sticky-label">
        {trackInfo ? (
          <TrackBadge trackInfo={trackInfo} scoreVersion={scoreVersion} />
        ) : (
          <div onClick={() => togglePlaylist(rowIndex)} className="playlistInfo">
            {title}
          </div>
        )}
      </td>
    )
  }
  const getHeader = () => {
    const badgeClass = cx('playerBadge', { guestName: guestCount > 0 })
    return (
      <tr>
        <th className="sticky-header corner">
          <GuestPlayerSetup
            compoundMatchSlug={compoundMatchSlug}
            isAllowNewPlayer={false}
            isPrintReady={isPrintReady}
          />
        </th>
        {colLabels.map(({ slug, title, score, scoreRank }, index) => (
          <th key={index} className="sticky-header">
            <div className="playerScore">
              <div className="matchRank">{index + 1}</div>
              <div className={badgeClass} title={slug}>
                {title}
              </div>
              <div className="scoreWrapper">
                <div className={cx('score', { [scoreRank || '']: true })}>{score}</div>
              </div>
            </div>
          </th>
        ))}
      </tr>
    )
  }
  const getPlaylistForRow = (rowIndex: number) => {
    for (let i = rowIndex; i >= 0; i--) {
      const { slug, trackInfo } = rowLabels[i]
      if (!trackInfo) {
        return slug
      }
    }
    return ''
  }
  const toggleSelectedScore = (row: number, col: number) => {
    const key = `${row}-${col}`
    setSelectedCells((prev) => {
      const newState = { ...prev }
      if (key in selectedCells) {
        delete newState[key]
      } else {
        newState[key] = true
      }
      return newState
    })
  }
  const getBody = () => {
    return dataRows.map((row, rowIndex) => {
      const { slug, trackInfo } = rowLabels[rowIndex]
      const isTrack = !!trackInfo
      if (isTrack && !playlistRowState[rowIndex]) {
        return null
      }
      const trackSlug = isTrack ? slug : undefined
      return (
        <tr key={rowIndex}>
          {getRowLabel(rowIndex)}
          {row.map((cell: LeaderboardCell, colIndex: number) => {
            const playlistSlug = getPlaylistForRow(rowIndex)
            const { slug: compoundPlayerSlug } = colLabels[colIndex]
            const trackPath: TrackPath = {
              compoundPlayerSlug,
              compoundMatchSlug,
              playlistSlug,
              trackSlug: trackSlug || '', // TODO: fix
            }
            const onClick = () => {
              if (isOwnedMatch && trackSlug) {
                toggleSelectedScore(rowIndex, colIndex)
              }
            }
            const onClose = () => {
              setTimeout(() => {
                toggleSelectedScore(rowIndex, colIndex)
              }, 50)
            }
            const isSelected = `${rowIndex}-${colIndex}` in selectedCells
            const className = cx('playerScore', { isPlaylist: !isTrack })
            return (
              <td key={colIndex}>
                <div className={className}>
                  {isSelected ? (
                    <PlayStatusDetails trackPath={trackPath} onClose={onClose} />
                  ) : (
                    cell.score > 0 && (
                      <div
                        onClick={onClick}
                        className={cx('score', { [cell.scoreRank || '']: true })}
                      >
                        {cell.score}
                      </div>
                    )
                  )}
                </div>
              </td>
            )
          })}
        </tr>
      )
    })
  }

  return (
    <div className="statsByTrackTable">
      <div className="table-container">
        <table className="table">
          <thead>{getHeader()}</thead>
          <tbody>{getBody()}</tbody>
        </table>
      </div>
    </div>
  )
}

export default StatsByTrackTable
